import * as React from "react"
import { cn } from "../../lib/utils"

function Card({ className, ...props }: React.ComponentProps<"section">) {
  return (
    <section
      data-slot="card"
      className={cn(
        "rounded-2xl border border-line bg-surface/80 shadow-card backdrop-blur-sm",
        className
      )}
      {...props}
    />
  )
}

interface CardHeaderProps extends Omit<React.ComponentProps<"header">, "title"> {
  title: React.ReactNode
  description?: React.ReactNode
  icon?: React.ReactNode
  iconClassName?: string
  actions?: React.ReactNode
}

function CardHeader({ title, description, icon, iconClassName, actions, className, ...props }: CardHeaderProps) {
  return (
    <header
      data-slot="card-header"
      className={cn("flex flex-wrap items-center justify-between gap-x-4 gap-y-3 px-5 pt-5 pb-4", className)}
      {...props}
    >
      <div className="flex min-w-0 items-center gap-3">
        {icon && (
          <span className={cn("grid size-8 shrink-0 place-items-center rounded-lg border border-line bg-raised text-brand [&_svg]:size-4", iconClassName)}>
            {icon}
          </span>
        )}
        <div className="min-w-0">
          <h2 className="truncate text-[15px] font-semibold tracking-tight text-fg">{title}</h2>
          {description && <p className="mt-0.5 text-[13px] text-fg-subtle">{description}</p>}
        </div>
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </header>
  )
}

function CardBody({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="card-body" className={cn("px-5 pb-5", className)} {...props} />
}

function CardFooter({ className, ...props }: React.ComponentProps<"footer">) {
  return (
    <footer
      data-slot="card-footer"
      className={cn("flex items-center gap-3 border-t border-line px-5 py-4", className)}
      {...props}
    />
  )
}

export { Card, CardHeader, CardBody, CardFooter }
